import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useListMediaQuery,
  useUploadMediaMutation,
  useDeleteMediaMutation,
  useLazyDownloadMediaQuery,
  type EventMediaType,
  type ListMediaResponse,
  type MediaSummary,
  type DownloadMediaResponse,
} from '@proaxiv/server-api'
import {
  FileDropZone,
  compressImageFile,
  isImageFile,
  MediaGallery,
  type MediaGalleryItem,
  type MediaGalleryKind,
} from '@proaxiv/ui'
import type { AppEvent } from '../store/eventsSlice'
import { formatLocalDateTime } from '../utils/datetime'
import { forceFileDownload } from '../utils/forceFileDownload'
import { downloadEventMediaZip } from '../utils/downloadEventMediaZip'
import { MAX_EVENT_MEDIA_BYTES, mediaTypeFromMime } from '../utils/eventMediaType'
import { Button } from './Button'
import { ConfirmationModal } from './ConfirmationModal'

// ── Icons ─────────────────────────────────────────────────────────────────────

const TrashIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
)

const MEDIA_TYPE_TO_GALLERY_KIND: Record<EventMediaType, MediaGalleryKind> = {
  PHOTO: 'image',
  VIDEO: 'video',
  AUDIO: 'audio',
  PDF: 'pdf',
}

interface Props {
  event: AppEvent
}

function uploadErrorMessage(err: unknown, t: (key: string) => string): string {
  const data = (err as { data?: { code?: string; message?: string } })?.data
  if (data?.code === 'EVENT_NOT_ENDED') return t('media_error_event_not_ended')
  if (data?.code === 'INVALID_ARGUMENT') return data.message || t('media_error_invalid')
  return data?.message || t('media_error_invalid')
}

/**
 * Organizer panel for uploading and browsing post-event media (EP-14) — images, videos, audio,
 * and PDFs that attended participants can later view/download from their ticket page.
 *
 * Files are uploaded to S3 via the admin media APIs; downloads use short-lived presigned URLs.
 */
export function EventMediaPanel({ event }: Props) {
  const { t } = useTranslation()
  const { data } = useListMediaQuery({ eventId: event.id })
  const [uploadMedia, { isLoading: uploading }] = useUploadMediaMutation()
  const [deleteMedia, { isLoading: deleting }] = useDeleteMediaMutation()
  const [triggerDownload] = useLazyDownloadMediaQuery()

  const [error, setError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<MediaSummary | null>(null)
  // Compression of large photos can take a few seconds before the mutation starts;
  // without this the dropzone looks idle and reads as a stuck/broken upload.
  const [processing, setProcessing] = useState(false)

  const items: MediaSummary[] = (data as ListMediaResponse | undefined)?.media ?? []
  const busy = uploading || processing

  async function handleFileSelected(file: File) {
    setError(null)
    const mediaType = mediaTypeFromMime(file.type)
    if (!mediaType) {
      setError(t('media_error_invalid'))
      return
    }
    if (file.size > MAX_EVENT_MEDIA_BYTES) {
      setError(t('media_error_invalid'))
      return
    }
    setProcessing(true)
    try {
      // Photos: same client-side compression as logos/posters (see compressImageFile).
      // Video / audio / PDF are left unchanged — the raster compressor can't help them.
      const toUpload = isImageFile(file) ? await compressImageFile(file) : file
      const formData = new FormData()
      // Third arg is required: FormData ignores Blob `.name` hacks and would otherwise send
      // the multipart filename as the literal string "blob".
      formData.append('file', toUpload, file.name)
      await uploadMedia({
        eventId: event.id,
        mediaType,
        body: formData as unknown as { file: Blob },
      }).unwrap()
    } catch (err) {
      setError(uploadErrorMessage(err, t))
    } finally {
      setProcessing(false)
    }
  }

  async function handleDownload(item: MediaSummary) {
    setError(null)
    try {
      const resp = (await triggerDownload({
        eventId: event.id,
        mediaId: item.id,
      }).unwrap()) as DownloadMediaResponse
      if (!resp.success || !resp.presignedUrl) {
        setError(t('media_download_error'))
        return
      }
      await forceFileDownload(resp.presignedUrl, resp.fileName ?? item.fileName)
    } catch {
      setError(t('media_download_error'))
    }
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return
    setError(null)
    try {
      await deleteMedia({ eventId: event.id, mediaId: pendingDelete.id }).unwrap()
      setPendingDelete(null)
    } catch {
      setError(t('media_error_invalid'))
      setPendingDelete(null)
    }
  }

  // Resolves the inline preview URL for a single item on first render (see
  // `MediaGalleryItem.getPreviewUrl`). Reuses the download endpoint — it returns both
  // `presignedUrl` (attachment) and `previewUrl` (inline) in one response.
  async function getPreviewUrl(item: MediaSummary): Promise<string> {
    const resp = (await triggerDownload({
      eventId: event.id,
      mediaId: item.id,
    }).unwrap()) as DownloadMediaResponse
    if (!resp.success || !resp.previewUrl) throw new Error('preview unavailable')
    return resp.previewUrl
  }

  async function handleDownloadAll() {
    setError(null)
    try {
      await downloadEventMediaZip(event.id)
    } catch {
      setError(t('media_download_all_error'))
    }
  }

  const galleryItems: MediaGalleryItem[] = items.map((item) => ({
    id: item.id,
    fileName: item.fileName,
    kind: MEDIA_TYPE_TO_GALLERY_KIND[item.mediaType],
    getPreviewUrl: () => getPreviewUrl(item),
    subtitle: `${t(`media_type_${item.mediaType.toLowerCase()}`)} · ${formatLocalDateTime(item.uploadedAt)}`,
  }))

  return (
    <div className="border-pax-purple/20 rounded-2xl border bg-white p-6 shadow-sm">
      <div className="border-pax-purple/20 mb-4 flex items-center justify-between border-b pb-2">
        <p className="text-pax-blue text-xs font-semibold tracking-wider uppercase">
          {t('media_panel_title')}
        </p>
      </div>

      <FileDropZone
        label={t('media_upload_label')}
        accept="image/*,video/*,audio/*,application/pdf"
        onFileSelected={handleFileSelected}
        disabled={busy}
        uploadLabel={busy ? t('media_uploading') : t('media_upload_label')}
        dropzoneClassName="border-pax-green/40 hover:border-pax-green text-pax-green flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-sm font-medium transition-colors focus-visible:outline-none"
        dropzoneActiveClassName="border-pax-green bg-pax-green/5"
      />
      <p className="text-pax-gray mt-2 text-xs">{t('media_upload_hint')}</p>
      {error && (
        <p className="text-pax-red mt-2 text-sm" role="alert">
          {error}
        </p>
      )}

      <div className="mt-5">
        <MediaGallery
          items={galleryItems}
          onDownload={(galleryItem) => {
            const item = items.find((m) => m.id === galleryItem.id)
            if (item) void handleDownload(item)
          }}
          downloadLabel={t('media_download')}
          previewUnavailableLabel={t('media_preview_unavailable')}
          onDownloadAll={() => void handleDownloadAll()}
          downloadAllLabel={t('media_download_all')}
          emptyLabel={<p className="text-pax-gray py-6 text-center text-sm">{t('media_empty')}</p>}
          className="gap-2"
          // Own row on mobile (stacked under the file info) so the action labels never get
          // squeezed into the ~15px sliver left after the icon + filename column, which used to
          // force the filename to truncate to nothing and wrap the actions illegibly (see
          // #media-mobile-layout).
          headerClassName="flex flex-col gap-3 sm:flex-row sm:items-center"
          infoClassName="flex min-w-0 flex-1 items-center gap-3"
          actionsClassName="flex shrink-0 items-center gap-2 self-end sm:ml-auto sm:self-auto"
          iconClassName="bg-pax-blue/10 text-pax-blue flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
          renderExtraActions={(galleryItem) => {
            const item = items.find((m) => m.id === galleryItem.id)
            if (!item) return null
            return (
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<TrashIcon />}
                onClick={() => setPendingDelete(item)}
                disabled={deleting}
                aria-label={t('media_delete')}
              >
                {t('media_delete')}
              </Button>
            )
          }}
        />
      </div>

      {pendingDelete && (
        <ConfirmationModal
          title={t('media_delete_confirm', { fileName: pendingDelete.fileName })}
          notify={false}
          showNotifyCheckbox={false}
          onConfirm={() => void handleConfirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  )
}
